import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { useServerLikes } from '@wwt/core/admin'
import { ModalTypes, useModalManager } from '@wwt/core/modals'
import {
	TutorialStatus,
	isTutorialAllowed,
	useTutorialStore,
	wwtTutorialLog
} from '@wwt/core/tutorial'
import { useSessionLikesStore } from '@wwt/store/sessionLikes/sessionLikesStore'

import { useCreateSetMutation, useDeleteSetMutation } from '@/entities/sets'
import { useShowTutorialRestartPopup } from '@/entities/tutorial'
import { useMobile } from '@/shared/lib/hooks/useBreakpoints'
import { useToast } from '@/shared/lib/hooks/useToast'

export const useTutorial = () => {
	const { t } = useTranslation('translation')
	const { toast } = useToast()
	const location = useLocation()

	const createSet = useCreateSetMutation()
	const deleteSet = useDeleteSetMutation()

	const showModal = useModalManager.use.showModal()
	const closeModal = useModalManager.use.closeModal()

	const showedModalId = useRef<string | null>(null)

	const { onOpen: showRestartPopup, onClose: closeRestartPopup } =
		useShowTutorialRestartPopup()

	const tutorialStatus = useTutorialStore.use.tutorialStatus()
	const setTutorialStatus = useTutorialStore.use.setTutorialStatus()
	const reset = useTutorialStore.use.reset()
	const setServerLikes = useServerLikes.use.setServerLikes()
	const restoreInitialValues = useServerLikes.use.restoreInitialValues()
	const resetSessionLikes = useSessionLikesStore.use.reset()

	const isMobile = useMobile() ?? false

	const onTutorialClose = useCallback(
		async (status: TutorialStatus) => {
			wwtTutorialLog('Close tutorial')

			if (showedModalId.current === null) {
				throw new Error('We cannot close tutorial video modal if it now showed')
			}

			closeModal(showedModalId.current)
			showedModalId.current = null

			setTutorialStatus(status)

			if (status === TutorialStatus.SKIPPED) {
				showRestartPopup()
			}
		},
		[closeModal, setTutorialStatus]
	)

	const showTutorialReviewModal = useCallback(
		(status: TutorialStatus) => {
			showedModalId.current = showModal(ModalTypes.TutorialReview, {
				onClose: () => {
					onTutorialClose(status)
				}
			})
		},
		[showModal, onTutorialClose]
	)

	const onMobileTutorialClose = useCallback(
		(status: TutorialStatus) => {
			if (status === TutorialStatus.SKIPPED) {
				onTutorialClose(status)
				return
			}

			onTutorialClose(status)

			showedModalId.current = showModal(ModalTypes.TutorialMobile, {
				onClose: () => onTutorialClose(TutorialStatus.SKIPPED),
				onTutorialClose: (status: TutorialStatus) => {
					onTutorialClose(status)
					showTutorialReviewModal(status)
				}
			})
		},
		[onTutorialClose, showModal, showTutorialReviewModal]
	)

	const openTutorial = () => {
		wwtTutorialLog('Try open tutorial', tutorialStatus, showedModalId.current)
		closeRestartPopup()

		if (
			showedModalId.current === null &&
			isTutorialAllowed(location.pathname, tutorialStatus)
		) {
			showedModalId.current = showModal(ModalTypes.TutorialVideo, {
				onClose: () => onTutorialClose(TutorialStatus.SKIPPED),
				onTutorialClose: isMobile ? onMobileTutorialClose : onTutorialClose,
				modalContentProps: {
					h: { xl: '31.25rem', md: '44.3125rem', base: '100%' }
				}
			})
		}
	}

	// Reset server likes and tutorial if tutorial going and init was called
	const initTutorial = useCallback(() => {
		wwtTutorialLog('Init tutorial', tutorialStatus, showedModalId.current)

		// Show tutorial on any status different from going
		if (tutorialStatus !== TutorialStatus.GOING) {
			openTutorial()
			return
		}

		wwtTutorialLog(
			'Reset server likes and tutorial status because `initTutorial` with tutorial GOING'
		)

		restoreInitialValues()
		reset()
	}, [tutorialStatus, restoreInitialValues, reset])

	// Set server likes after run tutorial and reset after stop
	useEffect(() => {
		if (tutorialStatus === TutorialStatus.GOING) {
			wwtTutorialLog('Disable server likes')
			setServerLikes(false)

			wwtTutorialLog('Reset session likes')
			resetSessionLikes()

			wwtTutorialLog('Create new set for tutorial')
			createSet.mutate('Tutorial')
			return
		}

		if (tutorialStatus === TutorialStatus.RESET) {
			reset()
			openTutorial()
			return
		}

		if (tutorialStatus === TutorialStatus.REVIEW) {
			wwtTutorialLog('Show tutorial completed toast')
			toast({
				layer: 'modals',
				description: t('toastTexts.tutorialEnded')
			})
		}
		// assuming that user can't complete tutorial faster than mutation
		if (createSet.data) {
			wwtTutorialLog('Delete created tutorial set')
			deleteSet.mutateAsync(createSet.data?.id).then(() => {
				createSet.reset()
				wwtTutorialLog('Restore server likes value')
				restoreInitialValues()
			})
		}
	}, [tutorialStatus, setServerLikes, restoreInitialValues, resetSessionLikes])

	return {
		initTutorial
	}
}
